import { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { maskPhoneBR, sanitizePhone } from "../../lib/phone";

export default function Contato() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "Coworking — Day Use", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/public/contact", { ...form, phone: sanitizePhone(form.phone) });
      toast.success("Mensagem enviada! Entraremos em contato em breve.");
      setForm({ name: "", email: "", phone: "", subject: "Coworking — Day Use", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao enviar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contato-page" className="pb-24">
      <section className="pt-24 pb-12 bg-[#0A1128]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-4">Fale Conosco</div>
          <h1 className="font-display text-5xl lg:text-6xl text-white font-light leading-tight">
            Vamos conversar sobre seu
            <span className="block italic text-[#D4AF37] mt-2">próximo escritório.</span>
          </h1>
        </div>
      </section>

      <section className="bg-[#0A1128] pb-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-1 space-y-6">
            <div className="card-marketing p-6">
              <div className="w-10 h-10 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-3">
                <MapPin size={18} className="text-[#D4AF37]" />
              </div>
              <div className="font-display text-white text-lg mb-1">Endereço</div>
              <div className="text-sm text-white/60">Piabetá, Magé<br/>Rio de Janeiro — RJ</div>
            </div>
            <div className="card-marketing p-6">
              <div className="w-10 h-10 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-3">
                <Phone size={18} className="text-[#D4AF37]" />
              </div>
              <div className="font-display text-white text-lg mb-1">WhatsApp</div>
              <a href="https://wa.me/5521975968405" target="_blank" rel="noreferrer" className="text-sm text-white/60 hover:text-[#D4AF37]">+55 21 97596-8405</a>
            </div>
            <div className="card-marketing p-6">
              <div className="w-10 h-10 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-3">
                <Mail size={18} className="text-[#D4AF37]" />
              </div>
              <div className="font-display text-white text-lg mb-1">E-mail</div>
              <a href="mailto:contato@vmcorporate.com.br" className="text-sm text-white/60 hover:text-[#D4AF37]">contato@vmcorporate.com.br</a>
            </div>
          </div>

          <form onSubmit={submit} className="md:col-span-2 card-marketing p-8 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-white/50 font-semibold">Nome completo *</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full mt-2 bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white placeholder-white/30 focus:border-[#D4AF37] outline-none"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-white/50 font-semibold">E-mail *</label>
                <input
                  required type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full mt-2 bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white placeholder-white/30 focus:border-[#D4AF37] outline-none"
                  data-testid="contact-email"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-white/50 font-semibold">Telefone</label>
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: maskPhoneBR(e.target.value) })}
                  className="w-full mt-2 bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white placeholder-white/30 focus:border-[#D4AF37] outline-none"
                  data-testid="contact-phone"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-white/50 font-semibold">Interesse</label>
                <select
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full mt-2 bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white outline-none focus:border-[#D4AF37]"
                  data-testid="contact-subject"
                >
                  <option className="bg-[#0A1128]">Coworking — Day Use</option>
                  <option className="bg-[#0A1128]">Coworking — Pacote 64h</option>
                  <option className="bg-[#0A1128]">Coworking — Pacote 96h</option>
                  <option className="bg-[#0A1128]">Escritório Executivo</option>
                  <option className="bg-[#0A1128]">Escritório Executivo Premium</option>
                  <option className="bg-[#0A1128]">Consultório</option>
                  <option className="bg-[#0A1128]">Sala de Reunião</option>
                  <option className="bg-[#0A1128]">Escritório Virtual</option>
                  <option className="bg-[#0A1128]">Tour gratuito</option>
                  <option className="bg-[#0A1128]">Outro</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-white/50 font-semibold">Mensagem *</label>
              <textarea
                required rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full mt-2 bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white placeholder-white/30 focus:border-[#D4AF37] outline-none resize-none"
                data-testid="contact-message"
              />
            </div>
            <button type="submit" disabled={loading} className="btn-gold inline-flex items-center gap-2 px-7 py-3 rounded-md text-sm disabled:opacity-50" data-testid="contact-submit">
              {loading ? "Enviando…" : <>Enviar mensagem <Send size={14} /></>}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
